/**
 * Timezone Context Provider (THUB-186)
 *
 * Lets a user choose the timezone every timestamp is displayed in, defaulting to
 * **America/Chicago (CST/CDT)** — the de-facto company default, which until now nothing
 * enforced.
 *
 * Instants are UTC end to end (THUB-272); this is purely a display concern. Stored
 * client-side in `localStorage`, mirroring how the theme preference works: no backend, no
 * table, no API. Tradeoff, stated on the ticket: the choice is per-browser, so a user on
 * two machines sets it in each. The upgrade path if cross-device is ever wanted is a
 * server-side user-preferences store.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_TIMEZONE,
  formatDateTime,
  formatRelativeTime,
  timezoneAbbreviation,
  supportedTimezones,
  setActiveTimezone,
  TIMEZONE_STORAGE_KEY,
  type DateStyle,
} from '../utils/formatDateTime';

// Shared with formatDateTime.activeTimezone() so the React and non-React readers
// can never drift apart.
const STORAGE_KEY = TIMEZONE_STORAGE_KEY;

export interface TimezoneContextValue {
  /** Active IANA zone, e.g. 'America/Chicago'. */
  timezone: string;
  setTimezone: (tz: string) => void;
  /** Current abbreviation for the active zone, e.g. 'CDT'. */
  abbreviation: string;
  /** Every zone offered by the selector. */
  available: string[];
  /** Format an absolute timestamp in the active zone. */
  format: (value: string | number | Date | null | undefined, style?: DateStyle) => string;
  /** Format a relative age ("5m ago") against the active zone. */
  formatRelative: (value: string | number | Date | null | undefined) => string;
}

const TimezoneContext = createContext<TimezoneContextValue | undefined>(undefined);

function isValidZone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function initialTimezone(): string {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    // Validate before trusting it: a hand-edited or stale value must not throw inside
    // every subsequent render.
    if (stored && isValidZone(stored)) return stored;
  } catch {
    /* localStorage unavailable (private mode / SSR) — fall through to the default. */
  }
  return DEFAULT_TIMEZONE;
}

export const TimezoneProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [timezone, setTimezoneState] = useState<string>(() => {
    const initial = initialTimezone();
    // Seed the non-React cache before first paint so both readers agree from the start.
    setActiveTimezone(initial);
    return initial;
  });

  const setTimezone = useCallback((tz: string) => {
    if (!isValidZone(tz)) return;
    setTimezoneState(tz);
    // Keep the non-React readers (formatDateTime.activeTimezone) in lockstep. Without this
    // the plain-function formatters used by AuditLogViewer / the dashboard panels kept
    // rendering the PREVIOUS zone until a full reload -- the selector appeared to do nothing.
    setActiveTimezone(tz);
    try {
      window.localStorage.setItem(STORAGE_KEY, tz);
    } catch {
      /* Persistence is best-effort; the in-memory choice still applies this session. */
    }
  }, []);

  // Keep multiple tabs consistent — changing the zone in one should not leave another
  // rendering a different one.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue && isValidZone(e.newValue)) {
        setTimezoneState(e.newValue);
        setActiveTimezone(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<TimezoneContextValue>(() => ({
    timezone,
    setTimezone,
    abbreviation: timezoneAbbreviation(timezone),
    available: supportedTimezones(),
    format: (v, style: DateStyle = 'full') => formatDateTime(v, timezone, style),
    formatRelative: (v) => formatRelativeTime(v, timezone),
  }), [timezone, setTimezone]);

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};

/**
 * Consume the timezone context.
 *
 * Deliberately does NOT throw when used outside a provider — it falls back to the company
 * default. A timestamp helper is used in dozens of leaf components and in tests that render
 * them in isolation; throwing would turn a missing provider into a blank page rather than a
 * slightly-wrong label.
 */
export const useTimezone = (): TimezoneContextValue => {
  const ctx = useContext(TimezoneContext);
  if (ctx) return ctx;
  return {
    timezone: DEFAULT_TIMEZONE,
    setTimezone: () => undefined,
    abbreviation: timezoneAbbreviation(DEFAULT_TIMEZONE),
    available: supportedTimezones(),
    format: (v, style: DateStyle = 'full') => formatDateTime(v, DEFAULT_TIMEZONE, style),
    formatRelative: (v) => formatRelativeTime(v, DEFAULT_TIMEZONE),
  };
};
